/*
 * The media rulebook — one copy of "what may be uploaded", shared by the users
 * app's `MediaUploadService` and the partner-area picker.
 *
 * This module is **data and predicates only**: no Angular, no HTTP, no
 * presentation. Everything here was lifted verbatim from
 * `projects/users/src/app/_shared/services/media-upload.service.ts`, which is
 * the shipped audience product's rulebook and therefore the source of truth;
 * where the partner picker had its own copy of a number, the users figure won.
 */

import { type MediaKind } from './cloudinary-upload.service';

/**
 * One platform's objection to an image, as **data**. The library never shows it
 * — it hands the warning back and the caller decides whether that is a toast, a
 * banner or a badge on the media tile.
 *
 * `code` and `suggestedAction` are single-valued today; they are unions rather
 * than strings so a second rule has to name itself when it arrives.
 */
export interface MediaPlatformWarning {
  platform: string;
  code: 'aspectRatio';
  message: string;
  suggestedAction: 'openEditor';
}

/** Warnings keyed by platform — at most one per platform. */
export type MediaPlatformWarnings = Partial<Record<string, MediaPlatformWarning>>;

/**
 * How an image came out of the rulebook.
 *
 * - `ok` — upload it, say nothing.
 * - `platformWarning` — upload it, but a selected platform will not render it
 *   well; {@link ImageValidationResult.warnings} says which and why.
 * - `large` — over the safe pixel buffer. Uploadable, but the caller should
 *   offer an optimised version.
 * - `tooLarge` — over the hard pixel ceiling.
 * - `error` — could not be prepared at all.
 */
export type ImageValidationStatus = 'ok' | 'large' | 'tooLarge' | 'error' | 'platformWarning';

export interface ImageValidationResult {
  isValid: boolean;
  error?: string;
  warnings?: MediaPlatformWarnings;
  status: ImageValidationStatus;
}

export interface VideoValidationResult {
  isValid: boolean;
  errorMessage?: string;
}

/** The pixel dimensions a rule is applied to. */
export interface ImageProperties {
  width: number;
  height: number;
  aspectRatio: number;
}

// --- Image rules --------------------------------------------------------------

/**
 * Per-platform image rules. Instagram is the only platform that rejects an
 * aspect ratio outright, so it is the only entry; `platform in
 * IMAGE_VALIDATION_RULES` is how callers ask whether a platform has any image
 * rule at all.
 */
export const IMAGE_VALIDATION_RULES = {
  instagram: {
    minAspectRatio: 3 / 4,
    maxAspectRatio: 1.91 / 1,
    tolerance: 0.01,
  },
} as const;

/** Cloudinary's hard ceiling for a transformable image. */
export const MAX_IMAGE_PIXELS = 36_000_000; // 36 MP

/** Above this on either edge an image still uploads, but socials may mangle it. */
export const IMAGE_SAFE_DIMENSION = 2048;

/** The compressed-image size gate — the same 10 MB the image cap below states. */
export const MAX_IMAGE_BYTES = 10 * 1024 * 1024;

/** Documents are capped independently of any platform rule. */
export const MAX_DOCUMENT_BYTES = 100 * 1024 * 1024;

// --- Video rules ---------------------------------------------------------------

export interface VideoPlatformRules {
  readonly aspectRatios: readonly number[];
  readonly maxDuration: number;
  readonly minDuration: number;
  /**
   * Optional because Instagram's feed rules declare none.
   *
   * ⚠️ The users app reads `rules.minResolution.width` unguarded
   * (media-upload.service.ts:794-801), so a video validated against
   * `instagram` throws `Cannot read properties of undefined` today and the
   * caller reports "Failed to process media". {@link validateVideoFile} guards
   * the access instead: a platform that declares no minimum has none to fail.
   */
  readonly minResolution?: { readonly width: number; readonly height: number };
  readonly maxFileSize: number;
  readonly requiredFormats: readonly string[];
}

/** Per-platform video rules, verbatim from the users app. */
export const VIDEO_VALIDATION_RULES: Readonly<Record<string, VideoPlatformRules>> = {
  facebook: {
    aspectRatios: [16 / 9, 9 / 16, 1 / 1],
    maxDuration: 14400, // 240 minutes
    minDuration: 1,
    minResolution: { width: 1200, height: 720 },
    maxFileSize: 10 * 1024 * 1024 * 1024, // 10GB
    requiredFormats: ['video/mp4', 'video/quicktime'],
  },

  instagram: {
    aspectRatios: [1.91 / 1, 1 / 1, 4 / 5],
    maxDuration: 60, // seconds
    minDuration: 3, // seconds
    maxFileSize: 100 * 1024 * 1024, // 100 MB
    requiredFormats: ['video/mp4', 'video/quicktime'],
  },
  'instagram-reels': {
    aspectRatios: [9 / 16, 16 / 9],
    maxDuration: 900, // 15 minutes
    minDuration: 3,
    minResolution: { width: 540, height: 960 }, // Reels min per Meta
    maxFileSize: 1 * 1024 * 1024 * 1024, // 1 GB
    requiredFormats: ['video/mp4', 'video/quicktime'],
  },
  // not yet supported
  'instagram-stories': {
    aspectRatios: [9 / 16],
    maxDuration: 60,
    minDuration: 1,
    minResolution: { width: 500, height: 1080 },
    maxFileSize: 4 * 1024 * 1024 * 1024, // 4GB
    requiredFormats: ['video/mp4', 'video/quicktime'],
  },
  'facebook-reels': {
    aspectRatios: [9 / 16],
    maxDuration: 90,
    minDuration: 3,
    minResolution: { width: 720, height: 1080 },
    maxFileSize: 4 * 1024 * 1024 * 1024, // 4GB
    requiredFormats: ['video/mp4'],
  },
  'facebook-stories': {
    aspectRatios: [9 / 16, 4 / 5],
    maxDuration: 60,
    minDuration: 1,
    minResolution: { width: 500, height: 1080 },
    maxFileSize: 4 * 1024 * 1024 * 1024, // 4GB
    requiredFormats: ['video/mp4', 'video/quicktime'],
  },
};

/** The formats a video must be in before any platform rule is consulted. */
export const SUPPORTED_VIDEO_TYPES: readonly string[] = ['video/mp4', 'video/quicktime'];

/** Below this a video file is treated as empty or truncated. */
export const MIN_VIDEO_BYTES = 1024;

// --- Type and size gates --------------------------------------------------------

/**
 * The file types accepted per kind — the users app's `AddMediaComponent`
 * `inputAccept` switch (add-media.component.ts:78-102), verbatim, and the list
 * the partner picker's `accept` attribute is built from.
 *
 * Note images are JPEG/PNG only: **GIFs cannot be uploaded from the device** in
 * either app, which is why the Giphy tab is the only route a GIF has in.
 */
export const ACCEPTED_TYPES: Readonly<Record<MediaKind, readonly string[]>> = {
  image: ['image/jpeg', 'image/png'],
  video: ['video/mp4', 'video/quicktime'],
  document: [
    'application/pdf',
    'application/ppt',
    'application/pptx',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/vnd.ms-powerpoint',
  ],
};

/**
 * The largest file each kind takes, in bytes.
 *
 * Derived from {@link calculateMaxFileSize} rather than restated, so the
 * partner picker's drop zone and the users app's `FileUploader` cannot drift.
 * The video figure is the minimum `maxFileSize` across
 * {@link VIDEO_VALIDATION_RULES} — Instagram's 100 MB.
 */
export const MAX_FILE_BYTES: Readonly<Record<MediaKind, number>> = {
  image: calculateMaxFileSize('image'),
  video: calculateMaxFileSize('video'),
  document: calculateMaxFileSize('document'),
};

/**
 * The size ceiling for one file of this kind.
 *
 * Takes a plain string rather than {@link MediaKind} because the users app
 * types its media by `MediaType`, which also carries `design` and `unknown`;
 * both fall to the image ceiling, as they did before this moved.
 */
export function calculateMaxFileSize(kind: MediaKind | (string & {})): number {
  switch (kind) {
    case 'document':
      return MAX_DOCUMENT_BYTES;
    case 'video':
      return Math.min(...Object.values(VIDEO_VALIDATION_RULES).map((rule) => rule.maxFileSize));
    case 'image':
    default:
      return MAX_IMAGE_BYTES;
  }
}

/**
 * Whether a file's MIME type belongs to the kind being uploaded.
 *
 * Deliberately looser than {@link ACCEPTED_TYPES}: this is the gate the upload
 * pipeline applies to a file that already got past a picker, so it asks only
 * "is this the right *sort* of thing" (`image/*`, `video/*`, `application/*`).
 * A picker that wants the narrow list checks {@link ACCEPTED_TYPES} itself.
 */
export function isFileTypeValid(file: File, kind: MediaKind | (string & {})): boolean {
  switch (kind) {
    case 'image':
      return file.type.match(/image.*/) !== null;
    case 'video':
      return file.type.match(/video.*/) !== null;
    case 'document':
      return file.type.match(/application.*/) !== null;
    default:
      return false;
  }
}

/** `10485760` → `"10MB"`, for a size-limit message. */
export function megabytes(bytes: number): string {
  return `${Math.round(bytes / (1024 * 1024))}MB`;
}
